import React, { useState } from "react";
import {
    Typography,
    Button,
    Checkbox,
    FormControlLabel,
    Paper,
    Box,
    Grid
} from '@material-ui/core';
import SearchCaseForm from "../Components/SearchCase";
import { getCaseReferenceNumber } from "../utils";
import FormError from "../Components/FormError";
import instance from "api/globalaxios";
import Swal from "sweetalert2";

const DailyProceeding = ({ classes }) => {
    // State to hold successfully searched case data
    const [searchedCase, setSearchedCase] = useState(null);

    // Form fields states
    const [isDisposed, setIsDisposed] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const resetForm = () => {
        setIsDisposed(false);
        setSelectedFile(null);
    };

    const handleSearchSuccess = (caseData) => {
        setSearchedCase(caseData);
        resetForm();
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const isFormValid = () => {
        return Boolean(searchedCase && selectedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        if (!searchedCase || !selectedFile) {
            setFormError("Please complete all required fields.");
            return;
        }

        try {
            setSubmitting(true);

            // Using FormData because we are transmitting a physical file binary
            const formData = new FormData();
            formData.append("dispute_id", searchedCase.id);
            formData.append("is_disposed", isDisposed ? "True" : "False");
            formData.append("file", selectedFile);

            const response = await instance.post('/bac/case-proceeding/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status === 200 || response.status === 201) {
                Swal.fire("Success", "Daily proceeding recorded successfully!", "success");
                // Clear state parameters
                setSearchedCase(null);
                resetForm();
            } else {
                Swal.fire("Error", "Submission failed. Please check entries.", "error");
            }
        } catch (err) {
            console.error("Submission error:", err);
            Swal.fire(
                "Error",
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                "An error occurred during submission.",
                "error"
            );
        } finally {
            setSubmitting(false);
        }
    };

    const refNo = getCaseReferenceNumber(searchedCase);

    return (
        <Box>

            {/* 1. Integrated Search Case Component Wrapper */}
            <Box mb={3}>
                <SearchCaseForm
                    classes={classes}
                    handleSearchSuccess={handleSearchSuccess}
                />
            </Box>

            {/* 2. Conditional render block: visible ONLY when searchedCase is loaded */}
            {searchedCase && (
                <Paper style={{ padding: 24, marginTop: 16 }}>
                    <Typography variant="h6" color="primary" gutterBottom={!refNo}>
                        {searchedCase.tiac_custom_dispute_id}
                    </Typography>
                    {refNo && (
                        <Typography variant="body2" color="textSecondary" gutterBottom style={{ overflowWrap: "anywhere" }}>
                            {refNo.label}: {refNo.value}
                        </Typography>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>

                            {/* Parties Overview breakdown panel */}
                            <Grid item xs={12}>
                                <Box bgcolor="#f9f9f9" p={2} borderRadius={4}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">Claimant</Typography>
                                            <Typography variant="body2"><strong>{searchedCase.claimant?.name || 'N/A'}</strong></Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">Respondent</Typography>
                                            <Typography variant="body2"><strong>{searchedCase.respondent?.name || 'N/A'}</strong></Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Disposed off flag */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={isDisposed}
                                            onChange={(e) => setIsDisposed(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Disposed off"
                                />
                            </Grid>

                            {/* Upload file section panel */}
                            <Grid item xs={12}>
                                <input
                                    accept="application/pdf,image/*,.doc,.docx"
                                    style={{ display: 'none' }}
                                    id="contained-button-file"
                                    type="file"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="contained-button-file" style={{ width: '100%' }}>
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        size="small"
                                        fullWidth
                                        style={{ height: '40px', textTransform: 'none' }}
                                    >
                                        {selectedFile ? `Selected: ${selectedFile.name}` : 'Upload Document *'}
                                    </Button>
                                </label>
                            </Grid>

                            {/* Proceeding Confirmation action submit button */}
                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    disabled={!isFormValid() || submitting}
                                    style={{ textTransform: 'none' }}
                                >
                                    {submitting ? "Submitting..." : "Submit Proceeding"}
                                </Button>
                                <FormError message={formError} />
                            </Grid>

                        </Grid>
                    </form>
                </Paper>
            )}
        </Box>
    );
};

export default DailyProceeding;
